import React, { useEffect, useMemo, useState } from "react";
import {
  useAssessmentSubjectScaleConfigGroupsForSubjectAssessmentConfig,
  useClassroomForSubjectAssessmentSetting,
  useImportSubjectConfiguration,
  useSubjectConfigApplySetttings,
  useSubjectListForSubjectAssessmentSetting,
} from "../../../../hooks/useAssessment";
import { useAssessmentScale } from "../../../../hooks/useAssessmentScale";
import Button from "../../../shared/Button";
import Select from "../../../shared/Select";
import swal from "sweetalert";
import { components } from "react-select";
import { URL } from "../../../../../../utils/Constants";
import Switch from "@material-ui/core/Switch";
import { Spinner } from "reactstrap";
import CustomModal from "../../../shared/CustomModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { DragIndicator } from "@mui/icons-material";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Tooltip } from "@mui/material";

export const commentOptions = [
  { label: "letter", value: "letter" },
  { label: "word", value: "word" },
];

const SubjectConfiguration = ({ payload, assessmentInfo, closeModal }) => {
  const [configData, setConfigData] = useState({
    subjects: [],
    scale: null,
    commentConfig: {
      hasComment: false,
      minLen: 10,
      maxLen: 30,
      textType: "word",
    },
  });
  const [tableData, setTableData] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [recordedSubjectIds, setRecordedSubjectIds] = useState([]);
  const [editedScaleId, setEditedScaleId] = useState(null);
  const [deletedItem, setDeletedItem] = useState([]);
  const [importSettings, setImportSettings] = useState(null);
  const [warningModal, setWarningModal] = useState(false);
  const [applySettinsModal, setApplySettingsModal] = useState(false);
  const [applySettingsTo, setApplySettingsTo] = useState([]);

  const { assessmentScale } = useAssessmentScale();
  const { subjectConfig: subjects } =
    useSubjectListForSubjectAssessmentSetting(payload);
  const { subjectAssessmentConfig, loading, updateSubjectConfiguraiton } =
    useAssessmentSubjectScaleConfigGroupsForSubjectAssessmentConfig(payload);
  const {
    classroomForSubjectAssessmentSetting,
    getClassroomForSubjectAssessmentSetting,
  } = useClassroomForSubjectAssessmentSetting(payload);

  const { loading: isApplying, applySettingsHandler } =
    useSubjectConfigApplySetttings();
  // const {
  //   data,
  //   loading: isImporting,
  //   importSubjectConfiguration,
  // } = useImportSubjectConfiguration();

  const classRoomForSubjectSettingOptions = useMemo(() => {
    if (classroomForSubjectAssessmentSetting?.length === 0) return [];

    return classroomForSubjectAssessmentSetting?.map((classroom) => ({
      label: `Class ${classroom?.yearLevelName} - ${classroom?.name}`,
      value: classroom.id,
      yearLevelId: classroom?.yearLevelId,
    }));
  }, [classroomForSubjectAssessmentSetting]);

  //convert array of subjects to object for easy access without looping
  const subjectsConfig = useMemo(() => {
    const map = {};

    if (subjects?.length) {
      subjects.forEach((item) => {
        map[item.id] = item;
      });
    }

    return map;
  }, [subjects]);

  //convert array of assementsScale to object for easy access without looping
  const assessmentsConfig = useMemo(() => {
    const map = {};

    if (assessmentScale?.length) {
      assessmentScale.forEach((item) => {
        map[item.id] = item;
      });
    }

    return map;
  }, [assessmentScale]);

  const assessmentScaleOptions = useMemo(() => {
    if (configData?.subjects?.length > 0) {
      const assessment = assessmentScale?.filter((item) => {
        if (item?.assessmentScaleTypeCode !== "sct-mrk") return true;

        const scale = item?.assessmentScaleMark;

        return configData.subjects.some((sb) => {
          const subject = subjectsConfig[sb.value];

          if (!subject) return false;

          if (subject.hasTheory && subject.hasPractical) {
            return scale?.practicalMax !== 0 && scale?.theoryMax !== 0;
          }

          if (subject.hasTheory && !subject.hasPractical) {
            return scale?.practicalMin === 0 && scale?.practicalMax === 0;
          }

          if (!subject.hasTheory && subject.hasPractical) {
            return scale?.theoryMin === 0 && scale?.theoryMax === 0;
          }

          return false;
        });
      });

      return assessment?.map((item) => ({
        label: item?.scaleName,
        value: item?.id,
      }));
    }

    return [];
  }, [assessmentScale, configData?.subjects, subjectsConfig]);

  const scaleOptions = useMemo(() => {
    if (!editRowId || !editedScaleId) return [];

    const savedScale = assessmentsConfig[editedScaleId];
    const subject = subjectsConfig[editRowId];

    if (!subject) return [];

    const isRecorded =
      recordedSubjectIds?.length > 0 && recordedSubjectIds.includes(editRowId);

    const matchesSubject = (scale) => {
      if (subject.hasTheory && subject.hasPractical) {
        return scale?.practicalMax !== 0 && scale?.theoryMax !== 0;
      }

      if (subject.hasTheory && !subject.hasPractical) {
        return scale?.practicalMin === 0 && scale?.practicalMax === 0;
      }

      if (!subject.hasTheory && subject.hasPractical) {
        return scale?.theoryMin === 0 && scale?.theoryMax === 0;
      }

      return false;
    };

    return (
      assessmentScale
        ?.filter((item) => {
          if (
            isRecorded &&
            item?.assessmentScaleTypeCode !==
              savedScale?.assessmentScaleTypeCode
          ) {
            return false;
          }

          return matchesSubject(item?.assessmentScaleMark);
        })
        .map((item) => ({
          label: item?.scaleName,
          value: item?.id,
        })) || []
    );
  }, [
    assessmentScale,
    editRowId,
    subjectsConfig,
    editedScaleId,
    recordedSubjectIds,
  ]);

  const subjectsOptions = useMemo(() => {
    if (!subjects?.length) return [];

    if (configData?.subjects?.length > 0) {
      const subject = subjectsConfig[configData?.subjects?.[0]?.value];

      const newSubjects = subjects?.filter(
        (item) =>
          item?.hasTheory === subject?.hasTheory &&
          item?.hasPractical === subject?.hasPractical,
      );

      return newSubjects?.map((item) => {
        const parts = [item?.name];

        if (item?.hasTheory) parts.push("Theory");
        if (item?.hasPractical) parts.push("Practical");

        return {
          label: parts.join(" - "),
          value: item?.id,
        };
      });
    }

    return subjects.map((item) => {
      const parts = [item?.name];

      if (item?.hasTheory) parts.push("Theory");
      if (item?.hasPractical) parts.push("Practical");

      return {
        label: parts.join(" - "),
        value: item?.id,
      };
    });
  }, [subjects, configData?.subjects]);

  //add items to table
  const handleAssignSubjectsToAssessmentScale = () => {
    if (!configData.subjects.length || !configData.scale?.value) {
      swal("Error", "Please select subjects and scale");
      return;
    }

    if (
      configData?.commentConfig?.hasComment &&
      Number(configData?.commentConfig?.minLen) >
        Number(configData?.commentConfig?.maxLen)
    ) {
      swal("Error", "Min Length cannot be greater than max length");
      return;
    }

    const newData = configData?.subjects?.map((item) => ({
      subjectId: item?.value,
      scaleId: configData.scale?.value,
      ...(configData.commentConfig?.hasComment
        ? {
            commentConfig: {
              maxLen: configData.commentConfig?.maxLen,
              minLen: configData.commentConfig?.minLen,
              textType: configData?.commentConfig?.textType,
            },
          }
        : {
            commentConfig: null,
          }),
    }));

    const existingSubjectIds = new Set(tableData.map((td) => td.subjectId));

    const subjectExists = newData.some((nd) =>
      existingSubjectIds.has(nd.subjectId),
    );

    if (subjectExists) {
      swal("Oops", "An assessment is already assigned to this subject");
      return;
    }

    setTableData((prev) => [...prev, ...newData]);

    setConfigData((prev) => ({
      ...prev,
      subjects: [],
      scale: null,
      commentConfig: {
        ...prev.commentConfig,
        hasComment: false,
      },
    }));

    //getClassroomForSubjectAssessmentSetting();
  };

  //remove items from table
  const handleRemove = (index, id) => {
    const newData = tableData?.filter((td, idx) => idx !== index);
    setTableData(newData);

    if (id) {
      setDeletedItem((prev) => [...prev, id]);
    }
  };

  //handle comments config
  const handleCommentConfigChange = (e) => {
    const { name, value } = e.target;

    setConfigData((prev) => ({
      ...prev,
      commentConfig: {
        ...prev.commentConfig,
        [name]: value,
      },
    }));
  };

  //custom Select option style
  const CustomOption = (props) => {
    const { data } = props;

    return (
      <components.Option {...props}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {data.levelType === "image" ? (
            <img
              src={URL.imageSelectURL + data.label}
              style={{ width: 20, marginRight: 10 }}
            />
          ) : (
            <span>{data.label}</span>
          )}
        </div>
      </components.Option>
    );
  };

  //custom Select display value style
  const CustomSingleValue = (props) => {
    const { data } = props;

    return (
      <components.SingleValue {...props}>
        <div style={{ display: "flex", alignItems: "center" }}>
          {data.levelType === "image" ? (
            <img
              src={URL.imageSelectURL + data.label}
              alt=""
              style={{
                width: 20,
                height: 20,
                objectFit: "cover",
                marginRight: 10,
                borderRadius: "50%",
              }}
            />
          ) : (
            <span>{data.label}</span>
          )}
        </div>
      </components.SingleValue>
    );
  };

  //render assessment scale data
  const renderAssessmentScaleData = (scaleId) => {
    const assessment = assessmentsConfig[scaleId];

    if (assessment?.assessmentScaleTypeCode === "sct-mrk") {
      return (
        <span style={{ whiteSpace: "nowrap" }}>
          Minimum Achievement:{" "}
          {assessment?.assessmentScaleMark?.minAchievementLevel ??
            Number(assessment?.assessmentScaleMark?.theoryMin ?? 0) +
              Number(assessment?.assessmentScaleMark?.practicalMin ?? 0)}
        </span>
      );
    }

    if (assessment?.assessmentScaleTypeCode === "sct-lvl") {
      const options = assessment?.assessmentScaleLevels?.map((opt) => ({
        label: opt?.level,
        value: opt?.id,
        levelType: opt?.levelType,
      }));

      return (
        <Select
          options={options}
          components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
          styles={{ control: { width: "240px" } }}
          isClearable={false}
        />
      );
    }

    if (assessment?.assessmentScaleTypeCode === "sct-grd") {
      const options = assessment?.assessmentScaleGradePoints?.map((opt) => ({
        label: opt?.grade,
        value: opt?.id,
      }));

      return (
        <Select
          options={options}
          styles={{ control: { width: "240px" } }}
          isClearable={false}
        />
      );
    }

    return null;
  };

  //handle edit table' asessment scale
  const handleEditAssessmentScale = (subjectId) => {
    if (!subjectId) return;

    const updatedData = tableData?.map((item) => {
      if (item?.subjectId !== subjectId) return item;

      return {
        ...item,
        scaleId: editedScaleId,
        isChanged: true,
      };
    });

    setTableData(updatedData);
    setEditedScaleId(null);
    setEditRowId(null);
  };

  //update subject configuration
  const handleSubmit = async () => {
    const subjectConfig = tableData;

    const params = {
      assessmentDetails: payload,
      subjectScaleConfigs: subjectConfig,
      deletedSubjects: deletedItem,
    };

    try {
      await updateSubjectConfiguraiton(params);
      setImportSettings(null);
      setDeletedItem([]);
      setWarningModal(false);
    } catch (error) {
      console.error("Update failed", error);
    }
  };

  //apply settings handler
  const handleApplySettingsTo = async () => {
    if (!applySettingsTo?.length) return;

    const applyTo = applySettingsTo?.map((item) => ({
      classroomId: item?.value,
      yearLevelId: item?.yearLevelId,
    }));

    const applyFrom = {
      assessmentId: payload?.assessmentId,
      educationLevelDivisionId: payload?.educationLevelDivisionId,
      educationLevelId: payload?.educationLevelId,
      classroomId: payload?.classroomId,
      yearLevelId: payload?.yearLevelId,
    };

    const params = {
      applyFrom,
      applyTo,
    };

    try {
      await applySettingsHandler(params);
      setApplySettingsTo([]);
      setApplySettingsModal(false);
    } catch (error) {
      console.error("Apply Settings Failed: ", error);
    }
  };

  //drag and drop handler
  const handleDragEnd = (result) => {
    if (!result?.destination) return;
    if (
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index == result.source.index
    ) {
      return;
    }

    const items = Array.from(tableData);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setTableData(items);
  };

  //populate existing data
  useEffect(() => {
    if (subjectAssessmentConfig?.subjectScaleConfigs?.length > 0) {
      setTableData(subjectAssessmentConfig?.subjectScaleConfigs);
      setRecordedSubjectIds(subjectAssessmentConfig?.recordedSubjects);
    }
  }, [subjectAssessmentConfig]);

  return (
    <>
      <div className="arr-form">
        <Select
          placeholder={"Select Subjects"}
          options={subjectsOptions?.filter((opt) =>
            tableData?.every((td) => td?.subjectId !== opt?.value),
          )}
          value={configData?.subjects}
          onChange={(option) =>
            setConfigData((prev) => ({
              ...prev,
              subjects: option,
              scale: null,
            }))
          }
          styles={{ control: { minWidth: "300px" } }}
          isMulti={true}
          isClearable={false}
        />

        <Select
          placeholder={"Select Assessment Scale"}
          options={assessmentScaleOptions}
          value={configData?.scale || null}
          onChange={(option) =>
            setConfigData((prev) => ({ ...prev, scale: option }))
          }
          styles={{ control: { minWidth: "300px" } }}
          isClearable={false}
          isDisabled={configData?.subjects?.length === 0}
        />

        <div style={{ gap: "8px" }} className="flex">
          <p style={{ margin: 0 }}>Comment</p>
          <Switch
            checked={configData?.commentConfig?.hasComment}
            name="hasComment"
            onClick={() => {
              setConfigData((prev) => ({
                ...prev,
                commentConfig: {
                  ...prev.commentConfig,
                  hasComment: !prev.commentConfig.hasComment,
                },
              }));
            }}
            inputProps={{ "aria-label": "controlled" }}
          />
        </div>

        {configData?.commentConfig?.hasComment && (
          <>
            <Select
              placeholder={"Select Type"}
              options={commentOptions}
              value={
                commentOptions?.find(
                  (opt) => opt.value === configData?.commentConfig?.textType,
                ) || null
              }
              onChange={(option) =>
                setConfigData((prev) => ({
                  ...prev,
                  commentConfig: {
                    ...prev.commentConfig,
                    textType: option?.value,
                  },
                }))
              }
              styles={{ control: { minWidth: "300px" } }}
              isClearable={false}
            />
            <div className="form-input">
              <input
                placeholder={`Minimum ${configData?.commentConfig?.textType}`}
                type="number"
                className="form-control"
                name="minLen"
                min={0}
                value={configData?.commentConfig?.minLen}
                onChange={handleCommentConfigChange}
                onKeyDown={(e) => {
                  if (e.key === "e" || e.key === "-") {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div className="form-input">
              <input
                type="number"
                min={0}
                name="maxLen"
                className="form-control"
                onKeyDown={(e) => {
                  if (e.key === "e" || e.key === "-") {
                    e.preventDefault();
                  }
                }}
                value={configData?.commentConfig?.maxLen}
                onChange={handleCommentConfigChange}
                placeholder={`Maximum ${configData?.commentConfig?.textType}`}
              />
            </div>
          </>
        )}
        <Button
          disabled={
            configData?.subjects?.length === 0 || !configData?.scale?.value
          }
          onClick={handleAssignSubjectsToAssessmentScale}
        >
          Add
        </Button>
      </div>

      <div style={{ margin: "16px 0" }} className="arr-note">
        <strong>Note: </strong>Once you select a subject of a specific type
        (Theory or Practical), you cannot select subjects of a different type.
        Please ensure all selected subjects are of the same type.
      </div>

      <div className="arr-heading">
        <h1 style={{ fontSize: "14px" }}>
          Class {assessmentInfo.yearLevel} - {assessmentInfo.classroom} -{" "}
          {assessmentInfo.assessmentName}
        </h1>
      </div>

      <div className="arr-table-container">
        <table className="arr-table">
          <thead>
            <tr>
              <th></th>
              <th>Subjects</th>
              <th>Assessment Scale</th>
              <th>Comments</th>
              <th>Preview</th>
              <th></th>
            </tr>
          </thead>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="list">
              {(provided) => (
                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                  {loading.read ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="arr-spinner">
                          <div className="loader">
                            <Spinner size={"md"} color="primary"></Spinner>
                          </div>
                          <div style={{ fontSize: "13px", marginTop: "15px" }}>
                            Please wait !! Processing.....
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : tableData?.length > 0 ? (
                    tableData?.map((item, index) => {
                      const isEditable = item?.subjectId === editRowId;
                      const isDisabled =
                        item?.subjectId &&
                        recordedSubjectIds?.length > 0 &&
                        recordedSubjectIds?.includes(item?.subjectId);

                      const subject = subjectsConfig[item.subjectId];

                      return (
                        <Draggable
                          key={`${item?.scaleId}-${item?.subjectId}`}
                          draggableId={String(item?.subjectId)}
                          index={index}
                        >
                          {(provided) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <td>
                                <div style={{ color: "#d1d5db" }}>
                                  <DragIndicator />
                                </div>
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                <div className="flex">{`${subject?.name}${subject?.hasTheory ? " - Theory" : ""}${subject?.hasPractical ? " - Practical" : ""}`}</div>
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {isEditable ? (
                                  <Select
                                    placeholder={"Select Assessment Scale"}
                                    options={scaleOptions}
                                    value={
                                      scaleOptions?.find(
                                        (opt) =>
                                          opt.value ===
                                          (editedScaleId ?? item?.scaleId),
                                      ) || null
                                    }
                                    onChange={(option) =>
                                      setEditedScaleId(option?.value)
                                    }
                                    styles={{ control: { minWidth: "200px" } }}
                                    isClearable={false}
                                  />
                                ) : (
                                  assessmentsConfig[item.scaleId]?.scaleName
                                )}
                              </td>
                              <td>
                                {item?.commentConfig &&
                                Object.keys(item?.commentConfig)?.length > 0 ? (
                                  <div
                                    className="flex"
                                    style={{ gap: "8px", flexWrap: "nowrap" }}
                                  >
                                    <span style={{ whiteSpace: "nowrap" }}>
                                      Min Length: {item?.commentConfig?.minLen}
                                    </span>
                                    <span style={{ whiteSpace: "nowrap" }}>
                                      Max Length: {item?.commentConfig?.maxLen}
                                    </span>
                                    <span style={{ whiteSpace: "nowrap" }}>
                                      Text Type: {item?.commentConfig?.textType}
                                    </span>
                                  </div>
                                ) : (
                                  "No Comment"
                                )}
                              </td>
                              <td>
                                {renderAssessmentScaleData(item?.scaleId)}
                              </td>
                              <td>
                                <div className="action-btns">
                                  {item?.groupId && !isEditable && (
                                    <Button
                                      onClick={() => {
                                        setEditRowId(item?.subjectId);
                                        setEditedScaleId(item?.scaleId);
                                      }}
                                      size="sm"
                                      variant="outline"
                                    >
                                      Edit
                                    </Button>
                                  )}

                                  {item?.groupId && isEditable && (
                                    <>
                                      <Button
                                        onClick={() => setEditRowId(null)}
                                        size="sm"
                                        variant="outline"
                                      >
                                        Cancel
                                      </Button>

                                      <Button
                                        onClick={() =>
                                          handleEditAssessmentScale(
                                            item?.subjectId,
                                          )
                                        }
                                        size="sm"
                                        variant="primary"
                                      >
                                        Save
                                      </Button>
                                    </>
                                  )}

                                  {!isEditable && (
                                    <Tooltip
                                      title={
                                        isDisabled
                                          ? "You cannot remove this scale configuration because it is either recorded or finalized in the recordings."
                                          : ""
                                      }
                                    >
                                      <div>
                                        <Button
                                          onClick={() =>
                                            handleRemove(index, item?.subjectId)
                                          }
                                          size="sm"
                                          variant="danger"
                                          disabled={isDisabled}
                                        >
                                          Remove
                                        </Button>
                                      </div>
                                    </Tooltip>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center" }}>
                        No Data Found
                      </td>
                    </tr>
                  )}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
      </div>

      <div style={{ justifyContent: "space-between" }} className="flex">
        <Button onClick={closeModal} variant="outline">
          Cancel
        </Button>

        <div className="action-btns">
          <Button
            onClick={() => {
              const isConfigUpdate = tableData?.some((item) => item?.groupId);
              if (isConfigUpdate) {
                setWarningModal(true);
              } else {
                handleSubmit();
              }
            }}
            disabled={loading.read || loading.update}
          >
            {loading.update ? (
              <Spinner size={"sm"} color="white" />
            ) : (
              "Save Settings"
            )}
          </Button>
          {subjectAssessmentConfig?.subjectScaleConfigs?.length > 0 && (
            <Tooltip title="This feature lets you apply the saved scale configured in this section to other relevant sections within the same class.">
              <div>
                <Button
                  onClick={() => setApplySettingsModal(true)}
                  variant="outline"
                  disabled={
                    subjectAssessmentConfig?.subjectScaleConfigs?.length === 0
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span>Apply To</span>
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </div>
                </Button>
              </div>
            </Tooltip>
          )}
        </div>
      </div>

      <CustomModal
        isOpen={warningModal}
        toggle={() => setWarningModal(false)}
        modalHeader={"Update Configuration"}
        children={
          <div>
            <p>
              Are you sure? This will update the assessment settings. Changes
              may affect existing assessments.
            </p>
            <div className="action-btns end">
              <Button onClick={() => setWarningModal(false)} variant="outline">
                Cancel
              </Button>
              <Button disabled={loading.update} onClick={handleSubmit}>
                {loading?.update ? (
                  <Spinner size={"sm"} color="white" />
                ) : (
                  "Update"
                )}
              </Button>
            </div>
          </div>
        }
      />

      <CustomModal
        isOpen={applySettinsModal}
        toggle={() => {
          setApplySettingsModal(false);
          setApplySettingsTo([]);
        }}
        modalHeader={"Apply Settings"}
        children={
          <div>
            <Select
              options={classRoomForSubjectSettingOptions?.filter(
                (opt) => opt.value !== payload?.classroomId,
              )}
              value={applySettingsTo}
              isMulti={true}
              styles={{ control: { minWidth: "200px" } }}
              placeholder={"Select Section"}
              onChange={(selectedOptions) =>
                setApplySettingsTo(selectedOptions)
              }
            />

            <div style={{ marginTop: "16px" }} className="action-btns end">
              <Button
                disabled={isApplying || applySettingsTo?.length === 0}
                size="sm"
                onClick={handleApplySettingsTo}
              >
                {isApplying ? (
                  <Spinner size="sm" color="white" />
                ) : (
                  "Apply Settings"
                )}
              </Button>
            </div>

            <div className="arr-note">
              <strong>Note: </strong>
              <ol
                style={{
                  padding: "10px 0px 0px 10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <li>
                  Please save the current changes before applying settings to
                  other section.
                </li>
                <li>
                  Please note that the existing configuration will be overridden
                  when using this feature. Additionally, if any scale of the
                  target sections are already recorded, those sections will not
                  be listed in the selection options.
                </li>
              </ol>
            </div>
          </div>
        }
      />
    </>
  );
};

export default SubjectConfiguration;
